import { useEffect, useState, type ReactNode } from "react";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";

import type { Row } from "./api";
import { useAdmin } from "./context";
import { icUrl, waitingChip, type Due } from "./format";

/** Title, one-line purpose, and a DEV label when the data is not the live app's. */
export function PageHead({ title, sub, right }: { title: ReactNode; sub?: ReactNode; right?: ReactNode }) {
  const { me } = useAdmin();
  return (
    <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
      <div>
        <h1 className="text-xl font-semibold text-ks-ink">{title}</h1>
        {sub ? <p className="mt-1 text-sm text-ks-grey-1">{sub}</p> : null}
      </div>
      <div className="flex items-center gap-2">
        {/* Live data carries no label (Damien 31 Oct: redundant); only a DEV build says it is not the live app. */}
        {me && me.env !== "prod" ? <Badge variant="outline" className="text-ks-warn">DEV data</Badge> : null}
        {right}
      </div>
    </div>
  );
}

export function StatusChip({ status }: { status: string | null | undefined }) {
  if (status === "paused") return <Badge variant="outline" className="text-ks-warn">Paused</Badge>;
  if (status === "on") return <Badge variant="outline" className="text-ks-pass">On plan</Badge>;
  return <Badge variant="secondary">No plan</Badge>;
}

/** Staff test account (an @ketosource.co address): kept out of counts and countdowns. */
export const TestChip = () => <Badge variant="secondary">Test</Badge>;

export const RefChip = () => <Badge variant="outline" className="text-ks-warn">Results below target</Badge>;

export function IcLink({ id, children }: { id: string | null | undefined; children?: ReactNode }) {
  if (!id) return null;
  return (
    <a href={icUrl(String(id))} target="_blank" rel="noopener noreferrer" className="text-ks-purple underline" data-nonmutating>
      {children ?? "Intercom ticket"}
    </a>
  );
}

export function LoadError({ what, error }: { what: string; error: unknown }) {
  return (
    <Alert>
      <AlertDescription>
        Could not {what}: {error instanceof Error ? error.message : String(error)}
      </AlertDescription>
    </Alert>
  );
}

export const Empty = ({ children }: { children: ReactNode }) => (
  <div className="py-8 text-center text-sm text-ks-grey-1">{children}</div>
);

export const Loading = () => (
  <div className="space-y-2">
    <Skeleton className="h-6 w-1/3" />
    <Skeleton className="h-24 w-full" />
  </div>
);

/**
 * The browser clock, corrected to the server's (`server_now` at load) and ticking once a second, so a
 * countdown counts to the same deadline on every screen however wrong this machine's clock is.
 */
export function useServerNow(serverNow: string | null | undefined, loadedAt: number): number {
  const offset = serverNow && loadedAt ? new Date(serverNow).getTime() - loadedAt : 0;
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  return now + (Number.isFinite(offset) ? offset : 0);
}

export const TONE: Record<Due["tone"], string> = { ok: "text-ks-ink", soon: "text-ks-warn", overdue: "text-ks-danger" };

/** "<Kind>: waiting on a reply · d hh:mm:ss left", linking to the queue (PA2, PA3). */
export function WaitingChip({ w, now, onOpen }: { w: Row | null | undefined; now: number; onOpen: () => void }) {
  const c = waitingChip(w, now);
  if (!c) return null;
  return (
    <Badge asChild variant="outline" className={`${TONE[c.tone]} h-auto whitespace-normal text-left tabular-nums`}>
      <button type="button" data-nonmutating onClick={(e) => { e.stopPropagation(); onOpen(); }}>
        {c.text}
      </button>
    </Badge>
  );
}
